import { plural } from '../../../shared/plural';
import { windowText } from '../bankRowText';
import { changeFieldLabel, changeValueText, type ChangeFormatContext } from '../changeHistoryFormat';
import { SOURCE_DEMO, SOURCE_PRE_ORDER, SOURCE_TMS } from '../recordSource';

/**
 * Oś czasu zlecenia (makieta FTL p. Mariusza, pkt 12, sekcja 6; Etap 1,
 * decyzja Bartka 15.10.2026) — z dziennika zmian (`UserOfferChange`).
 *
 * Tabela „Historia zmian” pokazuje każde pole osobno. Oś odpowiada na inne
 * pytanie: „co się działo ze zleceniem i co jeszcze przed nim” — więc
 * zbiera kamienie (stan, pojazd i warianty, alerty, giełdy i zapytania),
 * a zmiany samych danych (terminy, miasta, cena z importu) skleja w jeden
 * punkt na chwilę i źródło. Do tego trzy punkty spoza dziennika: wpis
 * zlecenia do banku, okno załadunku i okno rozładunku od klienta (plan,
 * gdy jeszcze przed nami) oraz „teraz”.
 *
 * Etykiety pól i wartości — te same co w historii (`changeHistoryFormat.ts`),
 * żeby oś i tabela mówiły jednym językiem. Nic nie wyliczamy: oś pokazuje
 * tylko to, co jest w dzienniku i w polach zlecenia.
 */
export type TimelineKind = 'record' | 'status' | 'vehicle' | 'alert' | 'trade' | 'data' | 'plan' | 'now';

export interface TimelineItem {
  key: string;
  /** Chwila (ISO). */
  at: string;
  kind: TimelineKind;
  title: string;
  /** Doprecyzowanie (okno, lista pól). */
  detail: string | null;
  author: string | null;
  /** Źródło wpisu słowem: „ręcznie”, „z TMS”, „system”… */
  source: string | null;
  reason: string | null;
  /** Punkt planu w przyszłości (okno klienta), nie zdarzenie. */
  planned: boolean;
}

/** Źródła wpisów — te same podpisy co w „Historii zmian”. */
export const TIMELINE_SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'ręcznie',
  IMPORT: 'z TMS',
  RULE: 'system',
  VIA: 'VIA',
  EXTERNAL: 'OneHub',
};

const FIELD_KINDS: Record<string, TimelineKind> = {
  planningStatus: 'status',
  acceptanceStatus: 'status',
  externalId: 'status',
  // Pętla zwrotna z TMS (15.10.2026) — numer i źródło zlecenia.
  sourceSystem: 'status',
  tmsLink: 'status',
  // Przyjęcie w TMS przy połączeniu (16.10.2026).
  viaWriteStatus: 'status',
  mergedIntoPreOrder: 'status',
  // Decyzja operatora przed zapisem do VIA (16.10.2026).
  notDuplicateConfirmed: 'status',
  preOrderLinkCandidate: 'alert',
  tmsLinkCandidate: 'alert',
  tmsOrderStatus: 'status',
  validUntil: 'status',
  assignedCarId: 'vehicle',
  // Termin wariantu i migawka rekomendacji (20.10.2026).
  variantValidUntil: 'vehicle',
  recommendationSnapshot: 'vehicle',
  candidate: 'vehicle',
  suggestionRejected: 'vehicle',
  assignmentBlockOverride: 'vehicle',
  // Obsadzenie mimo pewnego braku czasu (22.10.2026).
  assignedDespiteLate: 'vehicle',
  unassignRequested: 'vehicle',
  unassignDecision: 'vehicle',
  // Wnioski po wyjeździe auta (makieta back office v2, B6, 21.10.2026).
  vehicleChangeRequested: 'vehicle',
  vehicleChangeDecision: 'vehicle',
  tradeTransferRequested: 'trade',
  tradeTransferDecision: 'trade',
  assignedVehicleNumber: 'vehicle',
  tmsCarrierTitle: 'vehicle',
  vehicleOffer: 'vehicle',
  atRisk: 'alert',
  riskReason: 'alert',
  ostrzezenie: 'alert',
  replanSuggested: 'alert',
  tmsChangeAfterAssignment: 'alert',
  tmsCancellationSuspected: 'alert',
  tmsCancelled: 'alert',
  tmsCancellationDecision: 'alert',
  intakeCancellation: 'alert',
  // Rozliczenie zlecenia (26.10.2026, P4b).
  settlement: 'data',
  loadingTomorrowUnassigned: 'alert',
  vehicleChangedAfterAssignment: 'alert',
  tmsVehicleMismatch: 'alert',
  tmsChangeAcknowledged: 'alert',
  publikacja: 'trade',
  carrierInquiry: 'trade',
  // Wniosek o wyjątek od bramki publikacji (fala B, 21.10.2026).
  publicationExceptionRequested: 'trade',
  publicationExceptionDecision: 'trade',
  // Rejestr ofert (L12, 21.10.2026) i akceptacja przewoźnika (S-21.10-17).
  carrierOffer: 'trade',
  carrierAccepted: 'trade',
};

/** Rodzaj wpisu na osi; pole spoza mapy to zmiana danych. */
export function timelineKindOf(field: string): TimelineKind {
  return FIELD_KINDS[field] ?? 'data';
}

type Loose = Record<string, unknown>;

const EMPTY = '—';
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
const isId = (v: unknown): boolean => typeof v === 'string' && /^\d+$/.test(v);
const validTime = (v: unknown): v is string => typeof v === 'string' && !Number.isNaN(new Date(v).getTime());

/** Tytuł punktu z wpisu dziennika. */
export function changeTitle(change: Loose, ctx: ChangeFormatContext): string {
  const field = String(change.field ?? '');
  const before = changeValueText(field, change.oldValue, ctx);
  const after = changeValueText(field, change.newValue, ctx);
  if (field === 'assignedCarId') {
    if (before === EMPTY && after !== EMPTY) return `Obsadzono pojazdem ${after}`;
    if (before !== EMPTY && after === EMPTY) return `Odpięto pojazd ${before}`;
    if (before !== EMPTY && after !== EMPTY) return `Zmiana pojazdu: ${before} → ${after}`;
  }
  if (field === 'candidate') {
    if (change.oldValue == null && isId(change.newValue)) return `Dodano wariant: ${after}`;
    if (isId(change.oldValue) && change.newValue == null) return `Zwolniono wariant: ${before}`;
  }
  const label = changeFieldLabel(field);
  if (before === EMPTY) return `${label}: ${after}`;
  return `${label}: ${before} → ${after}`;
}

function recordTitle(source: unknown): string {
  if (source === SOURCE_TMS) return 'Zlecenie wpisane do banku z TMS';
  if (source === SOURCE_PRE_ORDER) return 'Prezlecenie założone w aplikacji';
  if (source === SOURCE_DEMO) return 'Zlecenie pokazowe (DEMO) wpisane do banku';
  return 'Zlecenie wpisane do banku';
}

/**
 * Punkty osi od najstarszego. Wpisy bez daty (wiersz listy, zanim przyjdą
 * szczegóły) pomijamy — nie ma ich gdzie postawić.
 */
export function orderTimeline(
  record: Loose | undefined,
  ctx: ChangeFormatContext,
  now: Date = new Date(),
): TimelineItem[] {
  if (!record) return [];
  const items: TimelineItem[] = [];
  const nowIso = now.toISOString();

  if (validTime(record.createdAt)) {
    items.push({
      key: 'record',
      at: record.createdAt,
      kind: 'record',
      title: recordTitle(record.sourceSystem),
      detail: null,
      author: null,
      source: null,
      reason: null,
      planned: false,
    });
  }

  const changes = Array.isArray(record.changes) ? (record.changes as Loose[]) : [];
  // Zmiany danych z jednej chwili i jednego źródła — jeden punkt z listą pól.
  const dataGroups = new Map<string, { item: TimelineItem; fields: string[] }>();
  changes.forEach((c, index) => {
    if (!validTime(c.changedAt)) return;
    const field = String(c.field ?? '');
    const kind = timelineKindOf(field);
    const author = text((c.changedBy as Loose | undefined)?.username);
    const source = c.source ? (TIMELINE_SOURCE_LABELS[String(c.source)] ?? String(c.source)) : null;
    const reason = text(c.reason);
    if (kind === 'data') {
      const groupKey = `${c.changedAt}|${String(c.source ?? '')}|${author ?? ''}`;
      const group = dataGroups.get(groupKey);
      const label = changeFieldLabel(field);
      if (group) {
        if (!group.fields.includes(label)) group.fields.push(label);
        if (reason && !group.item.reason) group.item.reason = reason;
        return;
      }
      const item: TimelineItem = {
        key: `data-${index}`,
        at: c.changedAt,
        kind,
        title: '',
        detail: null,
        author,
        source,
        reason,
        planned: false,
      };
      dataGroups.set(groupKey, { item, fields: [label] });
      items.push(item);
      return;
    }
    items.push({
      key: c.id !== undefined && c.id !== null ? `change-${String(c.id)}` : `change-${index}`,
      at: c.changedAt,
      kind,
      title: changeTitle(c, ctx),
      detail: null,
      author,
      source,
      reason,
      planned: false,
    });
  });
  for (const { item, fields } of Array.from(dataGroups.values())) {
    item.title = fields.length === 1 ? `Zmiana danych: ${fields[0]}` : `Zmiana danych (${plural(fields.length, 'pole', 'pola', 'pól')})`;
    item.detail = fields.length === 1 ? null : fields.join(', ');
  }

  const loadingFrom = record.loadingDateFrom ?? record.fromDate;
  if (validTime(loadingFrom)) {
    items.push({
      key: 'plan-loading',
      at: loadingFrom,
      kind: 'plan',
      title: 'Okno załadunku u klienta',
      detail: windowText(loadingFrom, (record.loadingDateTo as string | null | undefined) ?? null),
      author: null,
      source: null,
      reason: null,
      planned: new Date(loadingFrom).getTime() > now.getTime(),
    });
  }
  const unloadingTo = record.unloadingDateTo ?? record.toDate;
  const unloadingFrom = record.unloadingDateFrom ?? unloadingTo;
  if (validTime(unloadingFrom)) {
    items.push({
      key: 'plan-unloading',
      at: unloadingFrom,
      kind: 'plan',
      title: 'Okno rozładunku u klienta',
      detail: windowText(
        (record.unloadingDateFrom as string | null | undefined) ?? null,
        (unloadingTo as string | null | undefined) ?? null,
      ),
      author: null,
      source: null,
      reason: null,
      planned: new Date(unloadingFrom).getTime() > now.getTime(),
    });
  }

  items.push({
    key: 'now',
    at: nowIso,
    kind: 'now',
    title: 'Teraz',
    detail: null,
    author: null,
    source: null,
    reason: null,
    planned: false,
  });

  // Porządek po chwili; w tej samej chwili — kolejność dodania (stabilnie).
  return items
    .map((item, order) => ({ item, order, time: new Date(item.at).getTime() }))
    .sort((a, b) => a.time - b.time || a.order - b.order)
    .map((x) => x.item);
}

/** Dziennik w szczegółach ma sufit (API: 100 najnowszych wpisów). */
export const CHANGES_LIMIT = 100;
